import { useEffect, useState } from 'react';
import { BookEditor } from './BookEditor';
import type { BookRawJson } from '../../lib/bookSchema';
import { getBook, updateBook, type BookStatus, type UpsertBookPayload } from '../../lib/panelApi';

type Props = {
  accessToken: string;
  uuid: string;
  onDone: () => void;
};

export function EditBookFlow({ accessToken, uuid, onDone }: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initial, setInitial] = useState<{
    status: BookStatus;
    is_free: boolean;
    audio_cover_filename: string;
    book_cover_filename: string;
    audio_demo_filename: string;
    audio_full_filename: string;
    subtitle_full_vtt_filename: string;
    subtitle_full_json_filename: string;
    subtitle_demo_vtt_filename: string;
    subtitle_demo_json_filename: string;
    audio_full_minutes: number;
    pdf_filename: string;
    audio_text: string;
    content_json: BookRawJson;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await getBook(accessToken, uuid);
        const { uuid: _uuid, created_at: _createdAt, ...rest } = res;
        const content: BookRawJson = {
          metadata: rest.metadata ?? {},
          legal: rest.legal ?? {},
          faq: rest.faq ?? [],
          action_plan: rest.action_plan ?? [],
          mind_map: rest.mind_map ?? {},
          glossary: rest.glossary ?? [],
          simulation: rest.simulation ?? {},
          social_snaps: rest.social_snaps ?? [],
          key_takeaways: rest.key_takeaways ?? [],
          who_is_this_for: rest.who_is_this_for ?? [],
          unplay_assessment: rest.unplay_assessment ?? {},
        } as any;
        if (!cancelled)
          setInitial({
            status: (rest.status ?? 'draft') as BookStatus,
            is_free: !!rest.is_free,
            audio_cover_filename: rest.audio_cover_filename ?? '',
            book_cover_filename: rest.book_cover_filename ?? '',
            audio_demo_filename: rest.audio_demo_filename ?? '',
            audio_full_filename: rest.audio_full_filename ?? '',
            subtitle_full_vtt_filename: rest.subtitle_full_vtt_filename ?? '',
            subtitle_full_json_filename: rest.subtitle_full_json_filename ?? '',
            subtitle_demo_vtt_filename: rest.subtitle_demo_vtt_filename ?? '',
            subtitle_demo_json_filename: rest.subtitle_demo_json_filename ?? '',
            audio_full_minutes: typeof rest.audio_full_minutes === 'number' ? rest.audio_full_minutes : 0,
            pdf_filename: typeof rest.pdf_filename === 'string' ? rest.pdf_filename : '',
            audio_text: typeof rest.audio_text === 'string' ? rest.audio_text : '',
            content_json: content,
          });
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to load book';
        if (!cancelled) setError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, uuid]);

  if (loading) {
    return (
      <div className="card">
        <div className="sectionTitle">Edit Book</div>
        <div className="muted">Loading…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <div className="sectionTitle">Edit Book</div>
        <div className="errorBox" style={{ marginTop: 10 }}>
          {error}
        </div>
        <div className="row" style={{ marginTop: 12 }}>
          <button className="btn" onClick={onDone}>
            Back
          </button>
        </div>
      </div>
    );
  }

  if (!initial) {
    return (
      <div className="card">
        <div className="sectionTitle">Edit Book</div>
        <div className="muted">Book not found.</div>
        <div className="row" style={{ marginTop: 12 }}>
          <button className="btn" onClick={onDone}>
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <BookEditor
      accessToken={accessToken}
      title="Edit Book"
      bookUuid={uuid}
      initialContent={initial.content_json}
      initialFields={{
        status: initial.status,
        is_free: initial.is_free,
        audio_cover_filename: initial.audio_cover_filename,
        book_cover_filename: initial.book_cover_filename,
        audio_demo_filename: initial.audio_demo_filename,
        audio_full_filename: initial.audio_full_filename,
        subtitle_full_vtt_filename: initial.subtitle_full_vtt_filename,
        subtitle_full_json_filename: initial.subtitle_full_json_filename,
        subtitle_demo_vtt_filename: initial.subtitle_demo_vtt_filename,
        subtitle_demo_json_filename: initial.subtitle_demo_json_filename,
        audio_full_minutes: initial.audio_full_minutes,
        pdf_filename: initial.pdf_filename,
        audio_text: initial.audio_text,
      }}
      submitLabel="Save changes"
      onCancel={onDone}
      onSubmitBook={async (payload) => {
        await updateBook(accessToken, uuid, payload as UpsertBookPayload);
        onDone();
      }}
    />
  );
}

